export interface PayoutContext { symbol: string; duration: number; stake: string; currency: string }
export interface PayoutQuote {
  symbol: string; payout_percentage: number; payout_rate_bps: number;
  skew_active?: boolean; skew_penalty_bps?: number; tier_boost_bps?: number;
  applied_tier?: string; is_otc?: boolean; session_name?: string;
}
export const payoutContextKey = (c: PayoutContext) => JSON.stringify([c.symbol,c.duration,c.stake,c.currency]);

// Indicative only; never changes an executable locked quote or a contract.
export class PayoutFeed {
  private generation = 0;
  private expires = 0;
  private context = '';
  private quotes = new Map<string,PayoutQuote>();
  constructor(private now: () => number = Date.now) {}
  get(symbol: string): PayoutQuote | undefined { return this.now() < this.expires ? this.quotes.get(symbol) : undefined; }
  label(symbol: string): string { const q=this.get(symbol); return q ? Math.floor(q.payout_percentage).toFixed(0) : '—'; }
  clear() { this.quotes.clear(); this.expires=0; this.paint(); }
  paint() {
    if (typeof document === 'undefined') return;
    document.querySelectorAll<HTMLElement>('[data-live-payout-symbol]').forEach(el => {
      const lbl = this.label(el.dataset.livePayoutSymbol || '');
      if (lbl && lbl !== '—') {
        el.textContent = lbl + '%';
      }
      el.title = 'Indicative payout for selected duration, stake and wallet; final rate is locked before execution';
    });
  }
  async refresh(c: PayoutContext, symbols: string[], load: (symbols: string[]) => Promise<any>, current: () => PayoutContext): Promise<boolean> {
    const generation=++this.generation, key=payoutContextKey(c);
    if (this.context!==key || this.now()>=this.expires) this.clear();
    this.context=key;
    const isCurrent=()=>generation===this.generation && payoutContextKey(current())===key;
    try {
      const unique=[...new Set([c.symbol,...symbols])];
      const result: PayoutQuote[]=[];
      // Sequential bounded batches for future catalogues larger than 250 symbols.
      for(let i=0;i<unique.length;i+=200) {
        const batch=await load(unique.slice(i,i+200));
        if (!isCurrent()) return false;
        if (!Array.isArray(batch.quotes)) throw new Error('Invalid payout response');
        result.push(...batch.quotes);
      }
      const fresh=new Map<string,PayoutQuote>();
      for(const q of result) {
        if (!unique.includes(q.symbol) || !Number.isInteger(q.payout_rate_bps) || q.payout_rate_bps<100 || q.payout_rate_bps>10000
            || !Number.isFinite(q.payout_percentage) || Math.abs(q.payout_percentage-q.payout_rate_bps/100)>0.000001) throw new Error('Invalid payout rate');
        fresh.set(q.symbol,q);
      }
      if (fresh.size!==unique.length) throw new Error('Incomplete payout response');
      this.quotes=fresh; this.expires=this.now()+15_000; this.paint(); return true;
    } catch {
      if (!isCurrent()) return false;
      this.clear(); return true;
    }
  }
}
export const indicativePayouts = new PayoutFeed();
